import React, { useState, useEffect } from "react";
import { getFirestore, doc, setDoc } from "firebase/firestore";
import Container from '@mui/material/Container';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { StyledTableCell, StyledTableRow } from '../../utils/styled';
import { getPricesOnce } from "../../services/priceService";

const firestore = getFirestore();

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function ListPrices() {
    const [prices, setPrices] = useState([]);
    const [precioFilter, setPrecioFilter] = useState([]);
    const [searchTitle, setSearchTitle] = useState('');
    const [snackbarInfo, setSnackbarInfo] = useState({ text: '', type: 'info', open: false });

    // Modal state for Monto Fijo
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedPrice, setSelectedPrice] = useState(null);
    const [montoInput, setMontoInput] = useState('');
    const [montoFeriadoInput, setMontoFeriadoInput] = useState('');

    const fetchPrices = () => {
        getPricesOnce((data) => {
            const sorted = Array.isArray(data) ? [...data].sort((a, b) => (a.id ?? 0) - (b.id ?? 0)) : [];
            setPrices(sorted);
        });
    };

    useEffect(() => {
        fetchPrices();
    }, []);

    const handleSwitchChange = async (price) => {
        const isCurrentlyFixed = price.montoFijo === true || price.montoFijo === "true";

        if (!isCurrentlyFixed) {
            // Turning ON -> Open Modal to ask for amounts
            setSelectedPrice(price);
            const defaultSemana = price.montoFijoValor || price.hogar || '';
            const defaultFeriado = price.montoFijoFeriadoValor || price.feriado_domingo_hogar || defaultSemana;
            setMontoInput(defaultSemana);
            setMontoFeriadoInput(defaultFeriado);
            setModalOpen(true);
        } else {
            // Turning OFF -> set montoFijo to false
            try {
                const docRef = doc(firestore, 'precios', String(price.id));
                await setDoc(docRef, { montoFijo: false }, { merge: true });

                setPrices(prev => prev.map(p => p.id === price.id ? { ...p, montoFijo: false } : p));
                setSnackbarInfo({
                    open: true,
                    text: `"${price.descripcion || 'Servicio'}": Desactivado monto fijo (vuelve a % participación)`,
                    type: 'info'
                });
            } catch (error) {
                console.error("Error al desactivar montoFijo:", error);
                setSnackbarInfo({ open: true, text: "Error al actualizar tipo de precio", type: "error" });
            }
        }
    };

    const handleEditMontoValue = (price) => {
        setSelectedPrice(price);
        const defaultSemana = price.montoFijoValor || price.hogar || '';
        const defaultFeriado = price.montoFijoFeriadoValor || price.feriado_domingo_hogar || defaultSemana;
        setMontoInput(defaultSemana);
        setMontoFeriadoInput(defaultFeriado);
        setModalOpen(true);
    };

    const handleSaveModal = async () => {
        if (!selectedPrice) return;
        const valToSave = montoInput.trim() || selectedPrice.hogar || '0';
        const valFeriadoToSave = montoFeriadoInput.trim() || selectedPrice.feriado_domingo_hogar || valToSave;

        try {
            const docRef = doc(firestore, 'precios', String(selectedPrice.id));
            await setDoc(docRef, {
                montoFijo: true,
                montoFijoValor: String(valToSave),
                montoFijoFeriadoValor: String(valFeriadoToSave)
            }, { merge: true });

            setPrices(prev => prev.map(p => p.id === selectedPrice.id ? {
                ...p,
                montoFijo: true,
                montoFijoValor: String(valToSave),
                montoFijoFeriadoValor: String(valFeriadoToSave)
            } : p));

            setSnackbarInfo({
                open: true,
                text: `"${selectedPrice.descripcion}": Monto Fijo establecido en $ ${formatNumber(valToSave)} (D/F: $ ${formatNumber(valFeriadoToSave)})`,
                type: 'success'
            });

            setModalOpen(false);
            setSelectedPrice(null);
        } catch (error) {
            console.error("Error al guardar montoFijoValor:", error);
            setSnackbarInfo({ open: true, text: "Error al guardar el monto fijo", type: "error" });
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedPrice(null);
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarInfo({ text: '', type: 'info', open: false });
    };

    const displayTable = searchTitle !== "" ? precioFilter : prices;

    const formatNumber = (val) => {
        if (val === undefined || val === null || val === '') return '0';
        const num = Number(val);
        if (isNaN(num)) return `${val}`;
        return `${num.toLocaleString('es-PY')}`;
    };

    return (
        <Container component="main" maxWidth="xl">
            <Typography variant="h5" sx={{ mt: 3, mb: 2 }}>Listado de Precios</Typography>
            <TableContainer>
                <Table aria-label="tabla de precios">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Id</StyledTableCell>
                            <StyledTableCell>Descripción</StyledTableCell>
                            <StyledTableCell>Unidad de medida</StyledTableCell>
                            <StyledTableCell>Precio Cliente Hogar</StyledTableCell>
                            <StyledTableCell>Feriado/Domingo</StyledTableCell>
                            <StyledTableCell align="center">Monto Fijo Cuadrilla</StyledTableCell>
                            <StyledTableCell>Acciones</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {prices &&
                            displayTable.map((price) => {
                                const isFixed = price.montoFijo === true || price.montoFijo === "true";
                                const fixedVal = price.montoFijoValor || price.hogar || '0';
                                const fixedValFeriado = price.montoFijoFeriadoValor || price.feriado_domingo_hogar || fixedVal;
                                return (
                                    <StyledTableRow key={price.id}>
                                        <StyledTableCell>{price.id}</StyledTableCell>
                                        <StyledTableCell>{price.descripcion}</StyledTableCell>
                                        <StyledTableCell>{price.unidad_medida}</StyledTableCell>
                                        <StyledTableCell>$ {formatNumber(price.hogar)}</StyledTableCell>
                                        <StyledTableCell>$ {formatNumber(price.feriado_domingo_hogar)}</StyledTableCell>
                                        <StyledTableCell align="center">
                                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={isFixed}
                                                            onChange={() => handleSwitchChange(price)}
                                                            color="primary"
                                                        />
                                                    }
                                                    label={isFixed ? `$ ${formatNumber(fixedVal)} (D/F: $ ${formatNumber(fixedValFeriado)})` : "% Participación"}
                                                />
                                                {isFixed && (
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleEditMontoValue(price)}
                                                        title="Editar Monto Fijo"
                                                    >
                                                        <EditOutlinedIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </StyledTableCell>

                                        <StyledTableCell className="column-actions">
                                            <IconButton
                                                aria-label="edit"
                                                className="action__link"
                                                href={`/prices/${price.id}`}
                                                role="button"
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                );
                            })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Modal para ingresar/modificar el Monto Fijo */}
            <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="xs" fullWidth>
                <DialogTitle>Establecer Monto Fijo Cuadrilla</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Ingrese los montos fijos en Gs. que se pagarán a la cuadrilla por el servicio: <strong>{selectedPrice?.descripcion}</strong>.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="montoFijoInput"
                        label="Monto Fijo Semana (Gs.)"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={montoInput}
                        onChange={(e) => setMontoInput(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        id="montoFijoFeriadoInput"
                        label="Monto Fijo Domingo / Feriado (Gs.)"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={montoFeriadoInput}
                        onChange={(e) => setMontoFeriadoInput(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} color="inherit">
                        Cancelar
                    </Button>
                    <Button onClick={handleSaveModal} variant="contained" color="primary">
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbarInfo.open} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleCloseSnackbar} severity={snackbarInfo.type} sx={{ width: '100%' }}>
                    {snackbarInfo.text}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default ListPrices;
